'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from '@/components/i18n-provider';
import { useFullscreenNav } from './fullscreen-nav';
import { cn } from '@/lib/utils';

/**
 * Top-right auth pill for anonymous visitors. Occupies the slot the
 * `NotificationsFab` would otherwise sit in — the rest of the
 * workspace chrome stays identical to the authed layout.
 *
 * One button, not two. We detect whether this device has signed in
 * before (mirrors the decommissioned landing page's heuristic):
 *
 *   - `localStorage.robutler_has_logged_in === '1'`     → returning user → "Log in"
 *   - `localStorage.robutler_onboarding_done === '1'`   → returning user → "Log in"
 *   - otherwise                                          → first-time   → "Sign up"
 *
 * The detection happens client-side after hydration. To keep the
 * SSR markup stable we initialise with `false` (i.e. "Sign up") and
 * only flip after the effect runs — SSR + first paint both show
 * the same label, so no hydration warning.
 */
export function AnonAuthFab({ className }: { className?: string }) {
  const { openHref } = useFullscreenNav();
  const { t } = useTranslation();
  const [hasLoggedInBefore, setHasLoggedInBefore] = useState(false);
  useEffect(() => {
    try {
      const ls = window.localStorage;
      if (
        ls.getItem('robutler_has_logged_in') === '1' ||
        ls.getItem('robutler_onboarding_done') === '1'
      ) {
        setHasLoggedInBefore(true);
      }
    } catch {
      // private browsing or storage disabled — stick with the
      // "Sign up" default.
    }
  }, []);

  const target = hasLoggedInBefore ? '/login' : '/signup';
  const label = hasLoggedInBefore ? t('auth.login') : t('auth.signup');
  return (
    <div className={cn('pointer-events-auto', className)}>
      <button
        type="button"
        // Onboarding overlay points its "free account" hint here.
        data-onboarding-anchor="signup"
        onClick={() => openHref(target)}
        className="inline-flex h-10 items-center rounded-full bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
      >
        {label}
      </button>
    </div>
  );
}
